import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MapPin, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { useLocation, REGIONS, LocationContext } from "@/lib/locations";
import { useGeoLocation } from "@/hooks/use-geo-location";
import { useEffect, useState } from "react";

const BOOKING_URL = "/book";

const ServiceAreaPageInner = () => {
  const location = useLocation();
  const currentRegion = REGIONS[location.regionSlug];
  
  return (
    <div className="flex min-h-screen flex-col">
      <SEO 
        title={`Service Areas | Uplevel Cleaning — ${currentRegion.name} & Surrounding Regions`}
        description={`We provide top-rated house cleaning services across the Greater ${currentRegion.name} area. Check our coverage map.`}
        url="https://uplevelcleaning.com/locations"
      />
      <Navbar />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 bg-background overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent opacity-70" />
          </div>
          
          <div className="container relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-bold text-sm mb-6">
                <MapPin className="h-4 w-4" />
                <span>Greater {currentRegion.name} Coverage</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
                Professional Home Cleaning <br /> Across <span className="text-primary">{currentRegion.name}</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-10 leading-relaxed">
                Whether you're in the heart of the city or the quiet suburbs, our expert teams are just a click away. We bring the sparkle to every corner of the region.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="h-14 px-8 text-lg font-bold rounded-xl w-full sm:w-auto shadow-xl hover:shadow-2xl transition-all">
                  <a href={BOOKING_URL}>Book Your Clean Now</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-14 px-8 text-lg font-bold rounded-xl border-2 border-primary text-primary hover:bg-primary/10 hover:text-primary w-full sm:w-auto">
                  <a href={BOOKING_URL} aria-label="Get instant price for your service area">Get Instant Price</a>
                </Button>
              </div>
              <div className="mt-8 flex items-center justify-center gap-6 text-sm text-muted-foreground font-medium">
                <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Fully Insured</span>
                <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Background Checked</span>
                <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> 200% Guarantee</span>
              </div>
            </div>
          </div>
        </section>

        <ServiceAreaMap />
        


        {/* Neighborhood Highlight Section */}
        <section className="py-24 bg-muted/20">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Can't find your neighborhood?</h2>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  We are rapidly expanding our footprint. If you're within 30 miles of {currentRegion.name}, chances are we can reach you! Our teams are equipped with GPS tracking and local knowledge to ensure they arrive on time, every time.
                </p>
                <ul className="space-y-4 mb-10">
                  {[
                    "Apartment & Condo Cleaning",
                    "Luxury Estate Maintenance",
                    "Suburban Family Home Care",
                    "Downtown Loft Refresh"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 font-medium">
                      <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                        <ArrowRight className="h-3 w-3 text-primary" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Button asChild size="lg" variant="secondary" className="font-bold">
                  <a href="tel:9162995588">Call for Custom Coverage</a>
                </Button>
              </div>
              <div className="relative">
                <div className="aspect-video rounded-3xl overflow-hidden shadow-2xl border-8 border-background">
                  <img 
                    src="https://vibe.filesafe.space/1776798830316075366/assets/5e04562c-1143-4be5-91d2-98e30a94c625.png" 
                    alt={`${currentRegion.name} Neighborhood`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 bg-primary text-white p-8 rounded-2xl shadow-xl hidden md:block">
                  <p className="text-4xl font-bold mb-1">30+</p>
                  <p className="text-sm font-medium opacity-90">Zip Codes Served</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const ServiceAreaPage = () => {
  const { detectedRegion } = useGeoLocation();
  const [regionSlug, setRegionSlug] = useState<keyof typeof REGIONS>("sacramento-area");

  useEffect(() => {
    // Try to get the last visited region from localStorage, or use detected region
    const saved = localStorage.getItem("lastRegion");
    if (saved && REGIONS[saved as keyof typeof REGIONS]) {
      setRegionSlug(saved as keyof typeof REGIONS);
    } else if (detectedRegion && REGIONS[detectedRegion as keyof typeof REGIONS]) {
      setRegionSlug(detectedRegion as keyof typeof REGIONS);
    }
  }, [detectedRegion]);

  return (
    <LocationContext.Provider value={{
      slug: regionSlug,
      name: REGIONS[regionSlug].name,
      type: "region",
      regionSlug: regionSlug
    }}>
      <ServiceAreaPageInner />
    </LocationContext.Provider>
  );
};

export default ServiceAreaPage;
